package org.example.project.planner.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.planner.TimePickerTarget
import org.example.project.planner.minutesToTime
import org.example.project.planner.snapTo15Minutes
import org.example.project.planner.timeToMinutes
import org.example.project.ui.components.AppFilterChip
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TimePickerDialog(
    target: TimePickerTarget,
    currentTime: String,
    onConfirm: (String) -> Unit,
    onDismiss: () -> Unit
) {
    val shapes = LocalPlayfulShapes.current
    var selectedTime by remember { mutableStateOf(currentTime.ifBlank { "10:00" }) }
    val title = if (target == TimePickerTarget.EVENT_START) {
        "Zmień godzinę rozpoczęcia"
    } else {
        "Zmień godzinę zakończenia"
    }

    val presetHours = listOf("08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00")

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = AppIcons.Clock,
                    contentDescription = null,
                    tint = PlayfulColors.Coral,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = title,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.Bold,
                    color = PlayfulColors.TextPrimary
                )
            }
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
            ) {
                Text(
                    text = "Wybrana godzina: $selectedTime",
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = PlayfulColors.Coral
                )

                Spacer(modifier = Modifier.height(10.dp))

                // Quick delta buttons (+/- 15 min, +/- 1h)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    OutlinedButton(
                        onClick = {
                            val curMin = timeToMinutes(selectedTime)
                            selectedTime = minutesToTime(snapTo15Minutes(curMin - 60))
                        },
                        shape = shapes.buttonSmall,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("-1h", fontSize = 11.sp)
                    }
                    OutlinedButton(
                        onClick = {
                            val curMin = timeToMinutes(selectedTime)
                            selectedTime = minutesToTime(snapTo15Minutes(curMin - 15))
                        },
                        shape = shapes.buttonSmall,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("-15m", fontSize = 11.sp)
                    }
                    OutlinedButton(
                        onClick = {
                            val curMin = timeToMinutes(selectedTime)
                            selectedTime = minutesToTime(snapTo15Minutes(curMin + 15))
                        },
                        shape = shapes.buttonSmall,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("+15m", fontSize = 11.sp)
                    }
                    OutlinedButton(
                        onClick = {
                            val curMin = timeToMinutes(selectedTime)
                            selectedTime = minutesToTime(snapTo15Minutes(curMin + 60))
                        },
                        shape = shapes.buttonSmall,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("+1h", fontSize = 11.sp)
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                Text(
                    text = "Szybki wybór pełnych godzin:",
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = PlayfulColors.TextSecondary
                )
                Spacer(modifier = Modifier.height(6.dp))

                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    presetHours.forEach { preset ->
                        AppFilterChip(
                            label = preset,
                            selected = selectedTime == preset,
                            onClick = { selectedTime = preset }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedTextField(
                    value = selectedTime,
                    onValueChange = { selectedTime = it },
                    label = { Text("Wpisz ręcznie (GG:mm)", fontSize = 11.sp) },
                    singleLine = true,
                    shape = shapes.input,
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PlayfulColors.Coral,
                        unfocusedBorderColor = PlayfulColors.Border
                    )
                )
            }
        },
        confirmButton = {
            Button(
                onClick = { onConfirm(selectedTime) },
                shape = shapes.buttonSmall,
                colors = ButtonDefaults.buttonColors(
                    containerColor = PlayfulColors.Coral,
                    contentColor = PlayfulColors.TextOnPrimary
                )
            ) {
                Text("Zatwierdź", fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                shape = shapes.buttonSmall
            ) {
                Text("Anuluj", fontSize = 13.sp, color = PlayfulColors.TextSecondary)
            }
        },
        shape = shapes.dialog,
        containerColor = PlayfulColors.Surface
    )
}
